/**
 * ContactFormSuccess — focus-trapped confirmation state for a successful
 * contact-form submission. Renders a heading + body copy + optional
 * "Submit another" reset button.
 *
 * Mounts as a `role="status"` region with `aria-live="polite"` so
 * screen readers announce the confirmation without taking focus
 * away from the user's current navigation.
 *
 * @module @puffin-core/marketing-shell/contact/ContactFormSuccess
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.body
 * @param {string} [props.submissionId]      Echoed back to the user for support reference.
 * @param {string} [props.submissionIdLabel] Defaults to the `contactForm.referenceId` catalog word.
 * @param {string} [props.resetLabel]
 * @param {() => void} [props.onReset]       When omitted, the reset button is hidden.
 * @param {boolean} [props.autoFocusHeading=true]
 */
export default function ContactFormSuccess({
  title,
  body,
  submissionId,
  submissionIdLabel: hostSubmissionIdLabel,
  resetLabel,
  onReset,
  autoFocusHeading = true,
}) {
  const headingRef = useRef(null);
  // The reference label: the caller's, else the catalog's in the page's language — never an English
  // default parameter.
  const t = useMarketingShellTranslate();
  const submissionIdLabel = hostSubmissionIdLabel ?? t('contactForm.referenceId');

  useEffect(() => {
    if (autoFocusHeading && headingRef.current) {
      headingRef.current.focus();
    }
  }, [autoFocusHeading]);

  return (
    <div className="contact-form-success" role="status" aria-live="polite">
      <h2 ref={headingRef} tabIndex={-1} className="contact-form-success-title section-heading">
        {title}
      </h2>
      <p className="contact-form-success-body section-description">{body}</p>
      {submissionId ? (
        <p className="contact-form-success-reference">
          <span className="contact-form-success-reference-label">{submissionIdLabel}: </span>
          <code>{submissionId}</code>
        </p>
      ) : null}
      {onReset && resetLabel ? (
        <button type="button" className="btn btn-marketing-secondary" onClick={onReset}>
          {resetLabel}
        </button>
      ) : null}
    </div>
  );
}

ContactFormSuccess.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  submissionId: PropTypes.string,
  submissionIdLabel: PropTypes.string,
  resetLabel: PropTypes.string,
  onReset: PropTypes.func,
  autoFocusHeading: PropTypes.bool,
};
